import React, { useMemo } from "react";

import clsx from "clsx";
import moment from "moment";

import { useUser } from "@components/user-context";
import { type DataTypeProvider } from "@devexpress/dx-react-grid";
import { formatting } from "@north-beam/nb-common";

import { ACTUAL_WEEKLY_SPEND_ID, BUDGETED_WEEKLY_SPEND_ID } from "../constants";
import { type TableRowType } from "../performance-tracking-table";

export const WeeklySpendDataFormatter = ({
  value,
  row: _row,
  column,
}: DataTypeProvider.ValueFormatterProps) => {
  const { user } = useUser();
  const row = _row as TableRowType;

  const { weeklyMetrics } = row.data;
  const currentWeekNumber = useMemo(
    () =>
      weeklyMetrics.findIndex(({ dateRange }) => {
        const start = moment(dateRange.startDate);
        const end = moment(dateRange.endDate);
        return moment().isBetween(start, end, "week", "[]");
      }),
    [weeklyMetrics],
  );
  const hasCurrentWeek = currentWeekNumber !== -1;
  const currentWeek = weeklyMetrics[currentWeekNumber];

  const field =
    column.name === BUDGETED_WEEKLY_SPEND_ID ? "budgeted" : "actual";

  return (
    <div
      dir="rtl"
      className={clsx(
        "flex items-center h-full text-right overflow-hidden relative",
        {
          "text-cosmic+1": column.name === ACTUAL_WEEKLY_SPEND_ID,
          "text-interstellar+1": column.name !== ACTUAL_WEEKLY_SPEND_ID,
        },
      )}
    >
      {hasCurrentWeek && (
        <div className="flex items-center justify-end h-full z-1 w-28 absolute left-0 bg-v2-secondary">
          <div className="w-1 border-l border-cosmic-1 h-full" />
          <div className="pr-2 w-28">
            {formatting.formatCurrency(currentWeek[field], {
              precision: "approximate",
              code: user.currencies.currentCurrency,
            })}
          </div>
        </div>
      )}
      <div dir="ltr" className="flex items-center">
        {row.data.weeklyMetrics.map((week, index) => {
          const isLastWeek = index === row.data.weeklyMetrics.length - 1;
          return (
            <div key={index} className="flex items-end justify-end ml-2 w-24">
              <div
                className={clsx(
                  "font-medium text-sm font-mono mx-1 w-24 pr-2",
                  {
                    "text-cosmic": column.name === ACTUAL_WEEKLY_SPEND_ID,
                    "text-interstellar-1":
                      column.name !== ACTUAL_WEEKLY_SPEND_ID,
                  },
                )}
              >
                {formatting.formatCurrency(week[field], {
                  precision: "approximate",
                  code: user.currencies.currentCurrency,
                })}
              </div>
              {!isLastWeek && (
                <div className="text-gray text-md font-medium">|</div>
              )}
            </div>
          );
        })}
      </div>
      <div className="w-28" />
    </div>
  );
};
